/**
 * DORA Metrics tab
 *
 * The four DORA metrics. Deployment Frequency and Lead Time are measured
 * directly from merges to default branches of deploy-configured repos.
 * Change Failure Rate and MTTR are proxies derived from revert/hotfix/
 * rollback PRs until CI/CD or incident data is wired in, and are labeled
 * as such.
 */

import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import { periodWindow, toPeriod } from "~/lib/period";
import { BandScale, Columns, Figure, Ledger } from "~/components/charts";
import { api } from "~/lib/api";
import {
  DORA_BANDS,
  PROXY_FLAG,
  bandName,
  doraDek,
  doraHeadline,
  ledgerChange,
  type Band,
} from "../story";
import { fmtHoursFixed, fmtFixed, periodName, fmtDay } from "~/lib/format";
import { ed, btn, tbl } from "~/components/editorial";

interface DORAMetrics {
  period: {
    from: string;
    to: string;
    daysInPeriod: number;
  };
  configuredReposCount: number;
  deploymentFrequency: {
    value: number;
    dailyAverage: number;
    total: number;
    band: Band;
    bandLabel: string;
    trend: Array<{ date: string; value: number }>;
    previousValue: number;
    changePercent: number;
  };
  leadTimeForChanges: {
    value: number;
    median: number;
    p95: number;
    band: Band;
    bandLabel: string;
    trend: Array<{ date: string; value: number }>;
    previousValue: number;
    changePercent: number;
  };
  changeFailureRate: {
    value: number;
    failedDeployments: number;
    totalDeployments: number;
    band: Band;
    bandLabel: string;
    previousValue: number;
    changePercent: number;
    isProxy: true;
  };
  meanTimeToRecovery: {
    value: number;
    median: number;
    fixCount: number;
    band: Band;
    bandLabel: string;
    previousValue: number;
    changePercent: number;
    isProxy: true;
  };
  overallBand: Band;
  overallBandLabel: string;
  benchmarks: {
    deploymentFrequency: Record<string, string>;
    leadTime: Record<string, string>;
    changeFailureRate: Record<string, string>;
    mttr: Record<string, string>;
  };
}

const PROXY_NOTE =
  "Derived from revert, hotfix and rollback PRs, not CI/CD or incident data. Excluded from the overall band.";

export function DORATab({ range }: { range: string }) {
  const [searchParams] = useSearchParams();
  const win = periodWindow(toPeriod(range));
  const now = periodName(win.label);
  const teamId = searchParams.get("teamId") || "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["dora-metrics", range, teamId],
    queryFn: () =>
      api.get<{ data: DORAMetrics }>(
        `/metrics/dora?range=${range}${teamId ? `&teamId=${teamId}` : ""}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const metrics = data?.data;

  if (isLoading) return <div className={ed.loading}>Loading DORA metrics…</div>;
  if (error || !metrics) {
    return (
      <div className={ed.loading}>
        Couldn't load DORA metrics. Try again in a moment.
      </div>
    );
  }

  const thresholdsRows = (m?: DORAMetrics) => [
    {
      key: "df",
      label: "Deployment frequency",
      value: m ? `${fmtFixed(m.deploymentFrequency.value, 1)}/wk` : undefined,
      band: m?.deploymentFrequency.band,
      thresholds: metrics.benchmarks.deploymentFrequency,
    },
    {
      key: "lt",
      label: "Lead time for changes",
      note: "Average, PR opened to merged",
      value: m ? fmtHoursFixed(m.leadTimeForChanges.value) : undefined,
      band: m?.leadTimeForChanges.band,
      thresholds: metrics.benchmarks.leadTime,
    },
    {
      key: "cfr",
      label: "Change failure rate",
      flag: PROXY_FLAG,
      note: "Reverts and hotfixes over deploys",
      value: m ? `${fmtFixed(m.changeFailureRate.value)}%` : undefined,
      band: m?.changeFailureRate.band,
      thresholds: metrics.benchmarks.changeFailureRate,
    },
    {
      key: "mttr",
      label: "Mean time to recovery",
      flag: PROXY_FLAG,
      note: "Fix PR opened to merged",
      value: m
        ? m.meanTimeToRecovery.fixCount > 0
          ? fmtHoursFixed(m.meanTimeToRecovery.value)
          : "–"
        : undefined,
      band:
        m && m.meanTimeToRecovery.fixCount > 0
          ? m.meanTimeToRecovery.band
          : null,
      thresholds: metrics.benchmarks.mttr,
    },
  ];

  // Configuration required state
  if (metrics.configuredReposCount === 0) {
    return (
      <>
        <div className={ed.kicker}>DORA · {now}</div>
        <h1 className={ed.headlineCompact}>
          No repositories are marked as deploying on merge
        </h1>
        <p className={ed.dek}>
          DORA metrics count a merge to the default branch as a deploy, so they
          need to know which repositories ship to production that way. Mark them
          in GitHub settings and the numbers fill in here.
        </p>
        <Link to="/settings/github" className={btn.primary}>
          Configure repositories
        </Link>
        <div className={ed.sect}>The scale</div>
        <h2 className={ed.h2}>What each DORA band means</h2>
        <BandScale bands={DORA_BANDS} rows={thresholdsRows()} />
        <p className={ed.methodNarrow}>
          Thresholds from the{" "}
          <a href="https://dora.dev" target="_blank" rel="noopener noreferrer">
            DORA research program
          </a>
          .
        </p>
      </>
    );
  }

  const df = metrics.deploymentFrequency;
  const lt = metrics.leadTimeForChanges;
  const cfr = metrics.changeFailureRate;
  const mttr = metrics.meanTimeToRecovery;

  return (
    <>
      <div className={ed.kicker}>
        DORA · {now} · {bandName(metrics.overallBand)} band
      </div>
      <h1 className={ed.headlineCompact}>{doraHeadline(df.value, lt.median)}</h1>
      <p className={ed.dek}>{doraDek(metrics.overallBand, now)}</p>
      <div className={ed.byline}>
        <b>DORA</b> · {win.label}, compared with {win.prevLabel}.{" "}
        <span className="num">{fmtFixed(metrics.configuredReposCount)}</span>{" "}
        {metrics.configuredReposCount === 1 ? "repository" : "repositories"}{" "}
        configured as deploy-on-merge. Bands from the{" "}
        <a href="https://dora.dev" target="_blank" rel="noopener noreferrer">
          DORA research program
        </a>
        .
      </div>

      <Ledger
        hideMissingPrior
        columns="fit"
        items={[
          {
            label: "Deploys per week",
            value: fmtFixed(df.value, 1),
            prior: fmtFixed(df.previousValue, 1),
            note: `${fmtFixed(df.total)} in period`,
            ...ledgerChange(df.value, df.previousValue, 1),
          },
          {
            label: "Lead time",
            value: fmtHoursFixed(lt.value),
            prior: fmtHoursFixed(lt.previousValue),
            note: `average; median ${fmtHoursFixed(lt.median)}`,
            ...ledgerChange(lt.value, lt.previousValue, 1, "h"),
          },
          {
            label: "Change failure (proxy)",
            value: `${fmtFixed(cfr.value)}%`,
            prior: `${fmtFixed(cfr.previousValue)}%`,
            note: `${fmtFixed(cfr.failedDeployments)} of ${fmtFixed(cfr.totalDeployments)} deploys`,
            ...pointChange(cfr.value, cfr.previousValue),
          },
          {
            label: "Recovery (proxy)",
            value: mttr.fixCount > 0 ? fmtHoursFixed(mttr.value) : "–",
            prior: mttr.previousValue > 0 ? fmtHoursFixed(mttr.previousValue) : null,
            note: `${fmtFixed(mttr.fixCount)} fix PRs`,
            ...(mttr.fixCount > 0
              ? ledgerChange(mttr.value, mttr.previousValue, 1, "h")
              : {}),
          },
        ]}
      />

      <div className={ed.sect}>I. Where each metric falls</div>
      <h2 className={ed.h2}>
        Overall: {bandName(metrics.overallBand)}, from the two measured metrics
      </h2>
      <p className={ed.proseLinked}>
        Bands run from Elite on the left to Low on the right. The filled cell is
        where {now} falls. The two proxies are placed on the same scale for
        reference but don't count toward the overall band.
      </p>
      <BandScale
        bands={DORA_BANDS}
        rows={[
          {
            key: "overall",
            label: "Overall",
            note: "Deployment frequency and lead time only",
            value: bandName(metrics.overallBand),
            band: metrics.overallBand,
          },
          ...thresholdsRows(metrics),
        ]}
      />
      <p className={ed.methodNarrow}>
        Deploys are merges to the default branch of deploy-on-merge
        repositories. Lead time is PR opened to merged on those repositories.{" "}
        {PROXY_NOTE}
      </p>

      {(df.trend.length > 0 || lt.trend.length > 0) && (
        <>
          <div className={ed.sect}>II. Over time</div>
          <h2 className={ed.h2}>Deploys by day, lead time by week</h2>
          <div className={ed.halves}>
            {df.trend.length > 0 && (
              <Figure
                title="Deploys per day"
                sub={`${fmtFixed(df.dailyAverage, 1)} a day on average. Busiest day in color.`}
                method="Merges to the default branch of deploy-on-merge repositories, by merge date."
                table={{
                  columns: ["Date", "Deploys"],
                  rows: df.trend.map((d) => [fmtDay(d.date), d.value]),
                }}
              >
                <Columns
                  points={df.trend.map((d) => ({
                    key: d.date,
                    label: fmtDay(d.date),
                    value: d.value,
                  }))}
                  ariaLabel="Deploys over time"
                  unit="deploys"
                  emphasizeMax
                  width={560}
                />
              </Figure>
            )}
            {lt.trend.length > 0 && (
              <Figure
                title="Lead time per week"
                sub={`Median ${fmtHoursFixed(lt.median)}, p95 ${fmtHoursFixed(lt.p95)}. Slowest week in color.`}
                method="Average hours from PR opened to merged on deploy-on-merge repositories, grouped by the week of merge."
                table={{
                  columns: ["Date", "Lead time"],
                  rows: lt.trend.map((d) => [
                    fmtDay(d.date),
                    fmtHoursFixed(d.value),
                  ]),
                }}
              >
                <Columns
                  points={lt.trend.map((d) => ({
                    key: d.date,
                    label: fmtDay(d.date),
                    value: d.value,
                  }))}
                  ariaLabel="Lead time over time"
                  unit="hours"
                  emphasizeMax
                  width={560}
                  format={(v) => `${Math.round(v)}h`}
                />
              </Figure>
            )}
          </div>
        </>
      )}

      <div className={ed.sect}>III. The proxies</div>
      <h2 className={ed.h2}>How failure rate and recovery are estimated</h2>
      <dl className={styles.defs}>
        <dt>
          Change failure rate <span className={tbl.flag}>Proxy</span>
        </dt>
        <dd>
          <span className={ed.num}>{fmtFixed(cfr.failedDeployments)}</span> of{" "}
          <span className={ed.num}>{fmtFixed(cfr.totalDeployments)}</span> deploys
          were reverts, hotfixes or rollbacks, detected from the PR title and
          branch. It undercounts failures that never produced a fix PR.
        </dd>
        <dt>
          Mean time to recovery <span className={tbl.flag}>Proxy</span>
        </dt>
        <dd>
          {mttr.fixCount > 0 ? (
            <>
              <span className={ed.num}>{fmtFixed(mttr.fixCount)}</span> fix PRs
              took <span className={ed.num}>{fmtHoursFixed(mttr.value)}</span> on
              average and{" "}
              <span className={ed.num}>{fmtHoursFixed(mttr.median)}</span> at the
              median from opened to merged.
            </>
          ) : (
            <>No fix PRs in this period.</>
          )}{" "}
          True recovery time needs incident data (PagerDuty, Opsgenie) to
          measure from when the incident started.
        </dd>
      </dl>
    </>
  );
}

function pointChange(current: number, prior: number) {
  const d = Math.round(current - prior);
  if (d === 0) return { change: "no change" };
  return { change: `${d > 0 ? "+" : ""}${d} pts`, arrow: d > 0 ? "▲" : "▼" };
}

const styles = {
  defs: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "240px 1fr" },
    gap: "6px 24px",
    fontFamily: "sans",
    fontSize: "13.5px",
    lineHeight: 1.5,
    color: "ed.ink2",
    m: 0,
    maxWidth: "900px",
    "& dt": {
      fontWeight: 700,
      color: "ed.ink",
      pt: "8px",
      borderTopWidth: "1px",
      borderTopStyle: "solid",
      borderColor: "ed.rule",
    },
    "& dd": {
      m: 0,
      pt: "8px",
      borderTop: { base: "none", md: "1px solid" },
      borderColor: "ed.rule",
    },
  }),
};
